import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface PageHeaderCrumb {
  label: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
}

export interface PageHeaderProps {
  breadcrumb?: PageHeaderCrumb[];
  title: ReactNode;
  titleIcon?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

/**
 * Tiêu đề chuẩn của một màn quản trị (§9.5).
 *
 * Ứng dụng **không có thanh header toàn cục**: mỗi màn tự vẽ thẻ này. Avatar,
 * đổi chủ đề, cài đặt và đăng xuất nằm ở đáy thanh điều hướng (§4.1).
 *
 * Class `.page-header` giữ lại làm điểm móc cho CSS toàn cục; chủ đề `vibrant`
 * từng dùng nó để phủ lớp chuyển sắc, nay đã bỏ (ADR-UI-0007).
 *
 * Dấu phân cách giữa các mẩu đường dẫn là `›` (U+203A), không phải `/` hay `>`.
 */
export function PageHeader({
  breadcrumb,
  title,
  titleIcon,
  subtitle,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        'page-header flex flex-wrap items-start justify-between gap-4 rounded-lg border border-default bg-surface px-6 py-4 shadow-sm',
        className,
      )}
    >
      {/*
        min-w-0 là bắt buộc: thiếu nó thì `truncate` ở tiêu đề không ăn (§18.3 mục 11).
        `basis-72` + `flex-wrap` ở thẻ ngoài: khi cột tiêu đề hẹp hơn 18rem (768px, thanh điều hướng mở, nhiều
        nút) thì nhóm nút xuống dòng thay vì ép tiêu đề còn vài chữ.
      */}
      <div className="min-w-0 flex-1 basis-72">
        {breadcrumb && breadcrumb.length > 0 && (
          <nav className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-content-muted">
            {breadcrumb.map((crumb, index) => {
              const isLast = index === breadcrumb.length - 1;
              return (
                <span
                  key={index}
                  className={cn(
                    'inline-flex items-center gap-1',
                    isLast && 'font-medium text-content-secondary',
                  )}
                >
                  {index > 0 && <span aria-hidden>›</span>}
                  {crumb.icon}
                  {crumb.onClick ? (
                    <button
                      type="button"
                      onClick={crumb.onClick}
                      className="transition-colors hover:text-primary-600"
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    crumb.label
                  )}
                </span>
              );
            })}
          </nav>
        )}

        <div className="flex items-center gap-2">
          {titleIcon}
          <h1 className="truncate text-lg font-semibold text-content-primary">{title}</h1>
        </div>

        {subtitle && <p className="mt-0.5 text-xs text-content-muted">{subtitle}</p>}
      </div>

      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
